// Voice of Partner — the four-section feedback form.
//
// Used by both surfaces:
//   • PartnerFeedbackModal — auto-pops on dashboard mount until the
//     partner submits or dismisses for the session.
//   • PartnerFeedbackPage  — the sidebar "TrustLab Feedback" route.
//
// Sections (left-stripe colour codes the section type, matching the
// admin analytics colour vocabulary):
//   1. CSAT — five touchpoints, face emoji rating 1-5
//   2. NPS  — single 0-10 question with band-coloured selection
//   3. CES  — three statements, 7-point agree/disagree scale
//   4. Open feedback — two free-text textareas
//
// Visual identity uses MP.* tokens from the medical portal's shared
// stylesheet so the form sits inside the existing teal/cream chrome
// without a jarring brand shift. Bebas-Neue look is intentionally
// dropped — the rest of the portal is Outfit, and consistency wins.

import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { MP, MONO, DISPLAY } from '../medical/shared';
import {
  CSAT_DIMENSIONS,
  CES_STATEMENTS,
  faceFor,
} from './state';
import { useSubmitFeedbackMutation } from '../../../store/feedbackApi';

// Picks (or unpicks) a value in a sparse map: clicking the same
// option twice clears it, matching the prototype's "blank = skipped"
// convention. The backend silently drops blank dimensions, so the
// answer set always reflects what the partner intentionally rated.
const togglePick = (map, key, value) => {
  const next = { ...map };
  if (next[key] === value) delete next[key];
  else next[key] = value;
  return next;
};

const EMPTY_FORM = {
  csat: {},          // dimensionKey → 1..5
  nps: null,         // 0..10
  ces: {},           // statementKey → 1..7
  preferredLab: '',
  teamFeedback: '',
};

const PartnerFeedbackForm = ({ onSubmitted, onCancel, embedded = false }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitFeedback, { isLoading: submitting, error: submitError }] = useSubmitFeedbackMutation();

  const onPickCsat = (key, val) => setForm((f) => ({ ...f, csat: togglePick(f.csat, key, val) }));
  const onPickNps  = (val)      => setForm((f) => ({ ...f, nps: f.nps === val ? null : val }));
  const onPickCes  = (key, val) => setForm((f) => ({ ...f, ces: togglePick(f.ces, key, val) }));
  const onText     = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const canSubmit = form.nps !== null && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    try {
      const res = await submitFeedback({
        csat: form.csat,
        nps: form.nps,
        ces: form.ces,
        preferredLab: form.preferredLab,
        teamFeedback: form.teamFeedback,
      }).unwrap();
      onSubmitted?.(res?.response);
    } catch {
      // Inline error message rendered below the submit row — no toast,
      // no rethrow. RTK Query keeps the error in the mutation hook.
    }
  };

  // Reuse `f` as a short alias the JSX below already references.
  const f = form;

  return (
    <Box>
      {/* ─── 1. CSAT ─────────────────────────────────────────── */}
      <FormSection
        kind="csat"
        eyebrow={<><EyebrowBadge>CSAT</EyebrowBadge> Your satisfaction · {CSAT_DIMENSIONS.length} questions</>}
        title="How did we do, transactionally?"
        intro="Rate each touchpoint based on your experience this cycle. If a touchpoint didn't apply, leave it blank."
        embedded={embedded}
      >
        {CSAT_DIMENSIONS.map((d, i) => (
          <Box
            key={d.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
              alignItems: 'center',
              gap: 1.5,
              py: 1.75,
              borderBottom: i < CSAT_DIMENSIONS.length - 1 ? `1px solid ${MP.border}` : 'none',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: MP.dark, mb: 0.25 }}>
                {d.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: MP.textMuted, lineHeight: 1.4 }}>
                {d.desc}
              </Typography>
            </Box>
            <Stack direction="row" gap={0.75}>
              {[1, 2, 3, 4, 5].map((n) => {
                const face = faceFor(n);
                const selected = f.csat[d.key] === n;
                return (
                  <Tooltip key={n} title={face.tooltip} arrow>
                    <Box
                      role="button"
                      onClick={() => onPickCsat(d.key, n)}
                      sx={{
                        width: 38, height: 38,
                        border: `1.5px solid ${selected ? MP.yellow : MP.border}`,
                        bgcolor: selected ? MP.yellow : '#fff',
                        boxShadow: selected ? '0 0 0 3px rgba(240,180,41,.22)' : 'none',
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 18,
                        cursor: 'pointer',
                        transition: 'all .2s cubic-bezier(.2,.8,.2,1)',
                        '&:hover': {
                          borderColor: MP.teal,
                          bgcolor: selected ? MP.yellow : MP.tealLight,
                          transform: selected ? 'none' : 'scale(1.08)',
                        },
                      }}
                    >
                      {face.emoji}
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          </Box>
        ))}
      </FormSection>

      {/* ─── 2. NPS ──────────────────────────────────────────── */}
      <FormSection
        kind="nps"
        eyebrow={<><EyebrowBadge>NPS</EyebrowBadge> Loyalty · 1 question</>}
        title="Would you recommend us to a peer?"
        intro="On a scale of 0 to 10, how likely are you to recommend TrustLab to a fellow doctor, clinic, or partner organisation?"
        embedded={embedded}
      >
        <Box
          id="nps-row"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(11, 1fr)',
            gap: 0.5,
            mb: 1.5,
          }}
        >
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
            const band = n <= 6 ? 'detractor' : n <= 8 ? 'passive' : 'promoter';
            const selected = f.nps === n;
            const tone =
              band === 'detractor' ? { bg: MP.red,    fg: '#fff' }
              : band === 'passive' ? { bg: MP.yellow, fg: MP.dark }
              :                      { bg: MP.teal,   fg: '#fff' };
            return (
              <Box
                key={n}
                role="button"
                onClick={() => onPickNps(n)}
                sx={{
                  aspectRatio: '1 / 1',
                  border: `1.5px solid ${selected ? tone.bg : MP.border}`,
                  bgcolor: selected ? tone.bg : '#fff',
                  color: selected ? tone.fg : MP.dark,
                  borderRadius: '8px',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: MONO,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transform: selected ? 'scale(1.06)' : 'none',
                  transition: 'all .2s cubic-bezier(.2,.8,.2,1)',
                  '&:hover': !selected && {
                    borderColor: MP.teal,
                    bgcolor: MP.tealLight,
                  },
                }}
              >
                {n}
              </Box>
            );
          })}
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            fontFamily: MONO,
            fontSize: 11,
            letterSpacing: '0.04em',
            color: MP.textMuted,
            textTransform: 'uppercase',
          }}
        >
          <Box component="span">Not at all likely</Box>
          <Box component="span">Extremely likely</Box>
        </Stack>
      </FormSection>

      {/* ─── 3. CES ──────────────────────────────────────────── */}
      <FormSection
        kind="ces"
        eyebrow={<><EyebrowBadge>CES</EyebrowBadge> Effort · {CES_STATEMENTS.length} questions</>}
        title="How easy was it to work with us?"
        intro="Indicate how much you agree with each statement. 1 = Strongly Disagree, 7 = Strongly Agree."
        embedded={embedded}
      >
        {CES_STATEMENTS.map((s, i) => (
          <Box
            key={s.key}
            sx={{
              py: 2,
              borderBottom: i < CES_STATEMENTS.length - 1 ? `1px solid ${MP.border}` : 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                color: MP.dark,
                lineHeight: 1.5,
                fontStyle: 'italic',
                mb: 1.5,
                pl: 2,
                borderLeft: `3px solid ${MP.border}`,
              }}
            >
              {s.text}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.75,
                mb: 1,
              }}
            >
              {[1, 2, 3, 4, 5, 6, 7].map((n) => {
                const selected = f.ces[s.key] === n;
                return (
                  <Box
                    key={n}
                    role="button"
                    onClick={() => onPickCes(s.key, n)}
                    sx={{
                      border: `1.5px solid ${selected ? MP.purple : MP.border}`,
                      bgcolor: selected ? MP.purple : '#fff',
                      color:   selected ? '#fff'    : MP.dark,
                      borderRadius: '8px',
                      py: 1.25,
                      textAlign: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'all .2s cubic-bezier(.2,.8,.2,1)',
                      fontFamily: MONO,
                      fontSize: 13,
                      fontWeight: 700,
                      '&:hover': !selected && {
                        borderColor: MP.teal,
                        bgcolor: MP.tealLight,
                      },
                    }}
                  >
                    {n}
                  </Box>
                );
              })}
            </Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: '0.06em',
                color: MP.textMuted,
                textTransform: 'uppercase',
              }}
            >
              <Box component="span">Strongly Disagree</Box>
              <Box component="span">Strongly Agree</Box>
            </Stack>
          </Box>
        ))}
      </FormSection>

      {/* ─── 4. Open feedback ────────────────────────────────── */}
      <FormSection
        kind="notes"
        eyebrow={<><EyebrowBadge>QUAL</EyebrowBadge> Open feedback · 2 questions</>}
        title="Tell us, in your own words."
        intro="Optional — but specific stories help us act faster than ratings alone."
        embedded={embedded}
      >
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: MP.dark, mb: 0.75 }}>
              What's one thing that would make TrustLab your preferred lab?
            </Typography>
            <TextField
              multiline
              fullWidth
              minRows={3}
              value={f.preferredLab}
              onChange={(e) => onText('preferredLab', e.target.value)}
              placeholder="e.g. A dedicated WhatsApp channel for critical-value alerts…"
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: MP.dark, mb: 0.75 }}>
              Any specific feedback for our team?
            </Typography>
            <TextField
              multiline
              fullWidth
              minRows={3}
              value={f.teamFeedback}
              onChange={(e) => onText('teamFeedback', e.target.value)}
              placeholder="Praise, complaints, or concrete ideas — all welcome."
              sx={fieldSx}
            />
          </Box>
        </Stack>
      </FormSection>

      {/* Inline error band — only shown when the submit mutation
          itself returned an error. RTK Query keeps the message in
          submitError.data; we fall back to a generic line if the
          server didn't include one. Validation hints are surfaced
          via the disabled submit button (no NPS picked → grey). */}
      {submitError && (
        <Box
          sx={{
            mt: 2,
            px: 2, py: 1.5,
            border: `1px solid ${MP.red}`,
            bgcolor: MP.redLight,
            borderRadius: '8px',
            fontSize: 13,
            color: MP.red,
            fontWeight: 500,
          }}
        >
          {submitError?.data?.message || 'Could not submit feedback. Please try again.'}
        </Box>
      )}

      {/* Submit row */}
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mt: 2.5 }}
      >
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: 12,
            color: MP.textMuted,
            letterSpacing: '0.04em',
          }}
        >
          Confidential · Aggregated · Routed to leadership
        </Typography>
        <Stack direction="row" spacing={1.25}>
          {onCancel && (
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={submitting}
              sx={{
                color: MP.dark,
                borderColor: MP.border,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': { bgcolor: MP.bg, borderColor: MP.dark },
              }}
            >
              Maybe later
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={14} sx={{ color: MP.teal }} /> : null}
            sx={{
              bgcolor: MP.dark,
              color: MP.teal,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              px: 3,
              py: 1.25,
              '&:hover': { bgcolor: MP.teal, color: '#fff' },
              '&.Mui-disabled': { bgcolor: MP.border, color: MP.textMuted },
            }}
          >
            {submitting ? 'Submitting…' : 'Submit feedback →'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// ─── Section card with the coloured left-stripe identifier ──────
const SECTION_TONE = {
  csat:  MP.teal,
  nps:   MP.yellow,
  ces:   MP.purple,
  notes: MP.border,
};

const FormSection = ({ kind, eyebrow, title, intro, embedded, children }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      // The dialog version (`embedded`) drops its own card border so
      // the form reads as one continuous surface inside the modal;
      // the page version keeps the border so each section feels
      // distinct on the cream backdrop.
      border: embedded ? 'none' : `1px solid ${MP.border}`,
      borderRadius: embedded ? 0 : '14px',
      px: { xs: 2.5, md: 3.5 },
      py: { xs: 2.5, md: 3 },
      mb: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0,
        height: '100%', width: 4,
        bgcolor: SECTION_TONE[kind],
      },
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={1.25}
      sx={{
        fontFamily: MONO,
        fontSize: 11,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: MP.textMuted,
        mb: 0.75,
      }}
    >
      {eyebrow}
    </Stack>
    <Typography
      sx={{
        fontFamily: DISPLAY,
        fontSize: { xs: 18, md: 22 },
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: MP.dark,
        lineHeight: 1.1,
        mb: 0.75,
      }}
    >
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13, color: MP.textMuted, mb: 2.25, maxWidth: '60ch' }}>
      {intro}
    </Typography>
    {children}
  </Box>
);

const EyebrowBadge = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontWeight: 700,
      px: 0.875,
      py: 0.25,
      borderRadius: '4px',
      bgcolor: MP.bg,
      color: MP.dark,
      fontSize: 10,
    }}
  >
    {children}
  </Box>
);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.bg,
    borderRadius: '8px',
    fontSize: 14,
    color: MP.dark,
    '& fieldset': { borderColor: MP.border, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: MP.teal },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1.5 },
  },
};

export default PartnerFeedbackForm;
